const CPF_LENGTH = 11;
const CNPJ_LENGTH = 14;

const CNPJ_FIRST_DV_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_SECOND_DV_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

function stripDocumentFormatting(value: string): string {
  return (value ?? '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

function isCnpjDocument(raw: string): boolean {
  return /[A-Z]/.test(raw) || raw.length > CPF_LENGTH;
}

export function extractCpfDigits(value: string): string {
  return (value ?? '').replace(/\D/g, '').slice(0, CPF_LENGTH);
}

export function normalizeCnpj(value: string): string {
  return stripDocumentFormatting(value).slice(0, CNPJ_LENGTH);
}

export function normalizeCpfCnpj(value: string): string {
  const raw = stripDocumentFormatting(value);
  if (isCnpjDocument(raw))
  {
    return raw.slice(0, CNPJ_LENGTH);
  }

  return extractCpfDigits(raw);
}

export function isCompleteCpfCnpjDocument(value: string): boolean {
  const normalized = normalizeCpfCnpj(value);
  if (normalized.length === CNPJ_LENGTH)
  {
    return true;
  }

  if (normalized.length === CPF_LENGTH && !/[A-Z]/.test(normalized))
  {
    return true;
  }

  return false;
}

function getCnpjCharValue(char: string): number {
  const code = char.toUpperCase().charCodeAt(0);
  if (code >= 48 && code <= 57)
  {
    return code - 48;
  }

  if (code >= 65 && code <= 90)
  {
    return code - 48;
  }

  return -1;
}

function calculateCnpjCheckDigit(values: number[], weights: number[]): number {
  let sum = 0;
  for (let i = 0; i < weights.length; i++)
  {
    sum += values[i] * weights[i];
  }

  const remainder = sum % 11;
  if (remainder < 2)
  {
    return 0;
  }

  return 11 - remainder;
}

export function validateCNPJ(value: string): boolean {
  const cnpj = normalizeCnpj(value);
  if (cnpj.length !== CNPJ_LENGTH)
  {
    return false;
  }

  if (!/^[0-9A-Z]{12}\d{2}$/.test(cnpj))
  {
    return false;
  }

  const values = cnpj.split('').map((char) => getCnpjCharValue(char));
  if (values.some((value) => value < 0))
  {
    return false;
  }

  const firstCheckDigit = calculateCnpjCheckDigit(values.slice(0, 12), CNPJ_FIRST_DV_WEIGHTS);
  if (firstCheckDigit !== values[12])
  {
    return false;
  }

  const secondCheckDigit = calculateCnpjCheckDigit(values.slice(0, 13), CNPJ_SECOND_DV_WEIGHTS);
  return secondCheckDigit === values[13];
}

function validateCpf(digits: string): boolean {
  if (digits.length !== CPF_LENGTH)
  {
    return false;
  }

  if (/^(\d)\1+$/.test(digits))
  {
    return false;
  }

  const nums = digits.split('').map((digit) => Number(digit));
  let sum1 = 0;
  for (let i = 0; i < 9; i++)
  {
    sum1 += nums[i] * (10 - i);
  }

  let checkDigit1 = (sum1 * 10) % 11;
  if (checkDigit1 === 10)
  {
    checkDigit1 = 0;
  }

  if (checkDigit1 !== nums[9])
  {
    return false;
  }

  let sum2 = 0;
  for (let i = 0; i < 10; i++)
  {
    sum2 += nums[i] * (11 - i);
  }

  let checkDigit2 = (sum2 * 10) % 11;
  if (checkDigit2 === 10)
  {
    checkDigit2 = 0;
  }

  return checkDigit2 === nums[10];
}

export function validateCpfCnpj(value: string): boolean {
  const normalized = normalizeCpfCnpj(value);
  if (normalized.length === CPF_LENGTH && !/[A-Z]/.test(normalized))
  {
    return validateCpf(normalized);
  }

  if (normalized.length === CNPJ_LENGTH)
  {
    return validateCNPJ(normalized);
  }

  return false;
}

export function formatCpfInput(value: string): string {
  const digits = extractCpfDigits(value);
  if (digits.length <= 3)
  {
    return digits;
  }

  if (digits.length <= 6)
  {
    return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  }

  if (digits.length <= 9)
  {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  }

  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

export function formatCpfDisplay(value: string | null | undefined): string {
  if (!value?.trim())
  {
    return '';
  }

  return formatCpfInput(value);
}

function formatCnpjInput(value: string): string {
  const chars = normalizeCnpj(value);
  if (chars.length <= 2)
  {
    return chars;
  }

  if (chars.length <= 5)
  {
    return `${chars.slice(0, 2)}.${chars.slice(2)}`;
  }

  if (chars.length <= 8)
  {
    return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5)}`;
  }

  if (chars.length <= 12)
  {
    return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5, 8)}/${chars.slice(8)}`;
  }

  return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5, 8)}/${chars.slice(8, 12)}-${chars.slice(12)}`;
}

export function formatCpfCnpjInput(value: string): string {
  const raw = stripDocumentFormatting(value);
  if (isCnpjDocument(raw))
  {
    return formatCnpjInput(value);
  }

  return formatCpfInput(value);
}

export function formatCpfCnpjDisplay(value: string | null | undefined): string {
  if (!value?.trim())
  {
    return '';
  }

  return formatCpfCnpjInput(value);
}

export const CPF_CNPJ_FORMATTED_MAX_LENGTH = 18;

export function formatCpfCnpjDynamic(value: string): string {
  return formatCpfCnpjInput(value);
}

export function validateCpfCnpjField(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return 'Informe o CPF ou CNPJ do atendimento.';
  }

  if (!validateCpfCnpj(trimmed)) {
    return 'CPF ou CNPJ inválido.';
  }

  return null;
}

export function matchesCpfCnpjDocument(document: string, searchTerm: string): boolean {
  const normalizedDocument = normalizeCpfCnpj(document);
  const normalizedTerm = normalizeCpfCnpj(searchTerm);
  if (!normalizedTerm)
  {
    return false;
  }

  if (!/\d/.test(normalizedTerm))
  {
    return false;
  }

  return normalizedDocument.includes(normalizedTerm);
}

/** @deprecated Use normalizeCpfCnpj para CPF/CNPJ ou extractCpfDigits para CPF isolado. */
export function digitsOnly(value: string): string {
  return normalizeCpfCnpj(value);
}
